import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useReadContract, useWriteContract, useTransactionReceipt } from 'wagmi'
import { useAccount } from 'wagmi'
import bs58 from 'bs58';
import { formatEther, parseEther, hexToNumber, zeroAddress } from 'viem'

import { getDisplayNameFromFilename } from '../utils/GeneralUtilities';
import StickerContentUploader from './StickerContentUploader';
import ConnectAccount from '../components/ConnectAccount';
import { getPaymentMethods } from '../payments/PaymentMethods';
import {
    Board,
    StickerDesign,
    StickerDesignId,
    PublishedStickerData,
    PublishActivity,
    BaseActivity
} from '../utils/Primitives';
import StickerPublisherCertifications from './StickerPublisherCertifications';
import StickerMintOptions from './StickerMintOptions';
import { abi } from './StickerDesignsAbi'
import PublishedStickerList from './PublishedStickerList';
import { getOrLoadStickerDesign } from './StickerDesigns';
import StickerSharePanel from './StickerSharePanel';

const displayStage = (stage: PublishStages) => {
    switch (stage) {
        case 'new':
            return '';
        case 'uploading':
            return 'Uploading...';
        case 'uploaded':
            return 'Uploaded';
        case 'awaiting_tx':
            return 'Awaiting Transaction...';
        case 'tx_sent':
            return 'Transaction Sent...';
        case 'indexing':
            return 'Indexing...';
        case 'published':
            return 'Published!';
        case 'failed':
            return 'Failed';
        default:
            return '';
    }
}

export type PublishStages = 'new' | 'uploading' | 'uploaded' | 'awaiting_tx' | 'tx_sent' |  'indexing' | 'published'  | 'failed';
export type PublishErrors = 'none' | 'failed_execution' | 'failed_upload' | 'failed_validation' | 'failed_indexing';

const StickerContractAddress = process.env.NEXT_PUBLIC_STICKER_DESIGNS_CONTRACT_ADDRESS as `0x${string}` | null;
interface StickerCreatorProps {
  boardRef: React.MutableRefObject<Board>;
  handleDidClose: () => void;
  handleStartAddingToken: () => void;
  realtimeLatestStickerDesign: StickerDesign | null;
  onStickerPublished: (stickerData: PublishedStickerData, shouldReview: boolean) => void;
  isMiniApp?: boolean;
}

export default function StickerCreator({
  boardRef,
  handleDidClose,
  handleStartAddingToken,
  realtimeLatestStickerDesign,
  onStickerPublished,
  isMiniApp = false,
}: StickerCreatorProps) {
    const account = useAccount()
    const [publishStage, setPublishStage] = useState<PublishStages>('new');
    const [publishError, setPublishError] = useState<PublishErrors>('none');
    const [fileBlob, setFileBlob] = useState<Blob | null>(null);
    const [fileName, setFileName] = useState<string | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [imageSize, setImageSize] = useState<number | null>(null);
    const [imageContentType, setImageContentType] = useState<string | null>(null);
    const [imageIpfsHash, setImageIpfsHash] = useState<string | null>(null);
    const [stickerMetaIpfsHash, setStickerMetaIpfsHash] = useState<string | null>(null);
    const [aspectRatio, setAspectRatio] = useState<number | null>(null);
    const [stickerName, setStickerName] = useState<string | null>(null);
    const [publisherDidCertify, setPublisherDidCertify] = useState<boolean>(false);
    const [accountIsConnected, setAccountIsConnected] = useState<boolean>(false);
    const [accountIsPublisherInGoodStanding, setAccountIsPublisherInGoodStanding] = useState<boolean>(false);
    const [stickerHasBeenUploaded, setStickerHasBeenUploaded] = useState<boolean>(false);
    const { status: publishTransactionStatus, failureCount, failureReason, data: publishTransactionHash, writeContract, isSuccess: publishTransactionIsSuccess } = useWriteContract();
    const typedStickerAddress = StickerContractAddress ? StickerContractAddress : undefined;
    const checkAddress = account.address ? account.address : '0x0000000000000000000000000000000000000000' as `0x${string}`;
    const {error: goodStandingReadError, data: goodStandingReadData, status: goodStandingReadStatus, isFetched: goodStandingFetched} = useReadContract({abi: abi, address: typedStickerAddress, functionName: 'isPublisherInGoodStanding', args: [checkAddress]});
    const {error: publishFeeReadError, data: publishFeeReadData, status: publishFeeReadStatus, isFetched: pubFeeIsFetched} = useReadContract({abi: abi, address: typedStickerAddress, functionName: 'stickerRegistrationFee'});
    const {error: repFeeReadError, data: repFeeReadData, status: repFeeReadStatus, isFetched: repFeeIsFetched} = useReadContract({abi: abi, address: typedStickerAddress, functionName: 'publisherReputationFee'});
    const feesLoaded = goodStandingFetched && publishFeeReadData && repFeeReadData && repFeeIsFetched && pubFeeIsFetched ? true : false;
    const publishFee = pubFeeIsFetched && publishFeeReadData ? formatEther(publishFeeReadData) : null;
    const publisherReputationFee = repFeeIsFetched && repFeeReadData ? formatEther(repFeeReadData) : null;
    const [nextStickerFee, setNextStickerFee] = useState<BigInt>(BigInt(0));
    const [displayNextStickerFee, setDisplayNextStickerFee] = useState<string | null>(null);
    const {data: publishTransactionResult, isSuccess: publishTransactionHashIsFetched} = useTransactionReceipt({
        hash: publishTransactionHash,
        query: {
            enabled: publishTransactionIsSuccess,
            // Prevent continuous polling
            refetchInterval: 0,
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            refetchOnReconnect: false
        }
    });
    const [newStickerId, setNewStickerId] = useState<number | null>(null);
    const processedStickerIdRef = useRef<boolean>(false);
    const [holderAddress, setHolderAddress] = useState<string>('');
    const [premiumPrice, setPremiumPrice] = useState<number>(0);
    const [paymentCoin, setPaymentCoin] = useState<number>(0);
    const [publishedStickers, setPublishedStickers] = useState<StickerDesign[]>([]);
    const [isPublishing, setIsPublishing] = useState<boolean>(false);
    const [stickerUrl, setStickerUrl] = useState<string | null>(null);
    const [stickerDescription, setStickerDescription] = useState<string | null>(null);
    const [transactionError, setTransactionError] = useState<string | null>(null);
    const paymentMethods = getPaymentMethods({ boardRef });
    const paymentMethod = paymentMethods[paymentCoin];
    const paymentMethodDecimals = paymentMethod ? paymentMethod.decimals : 18;
    const [pendingActivities, setPendingActivities] = useState<PublishActivity[]>([]);

    // Define resetForm first before any other functions
    const resetForm = useCallback(() => {
        setStickerMetaIpfsHash(null);
        setPublisherDidCertify(false);
        setStickerHasBeenUploaded(false);
        setStickerName(null);
        setImageIpfsHash(null);
        setImagePreview(null);
        setImageSize(null);
        setImageContentType(null);
        setFileBlob(null);
        setFileName(null);
        setAspectRatio(null);
        setHolderAddress('');
        setPremiumPrice(0);
        setPaymentCoin(0);
        setPublishError('none');
        setNewStickerId(null);
        setIsPublishing(false);
        setStickerUrl(null);
        setStickerDescription(null);
    }, []);

    const handleClose = useCallback(() => {
        setTransactionError(null);
        if (newStickerId) {
            setPublishStage('new');
            resetForm();
            handleDidClose();
            return;
        }
        handleDidClose();
    }, [handleDidClose, newStickerId, resetForm]);

    // Add a function to manage pending activities
    const addPendingActivity = useCallback((activity: Omit<PublishActivity, 'id' | 'timestamp' | 'type' | 'isPending'>) => {
        setPendingActivities(current => {
            // Don't add if we already have this metadata CID
            if (current.some(a => a.metadataCID === activity.metadataCID)) {
                return current;
            }

            const newActivity: PublishActivity = {
                id: `pending-${Date.now()}`,
                timestamp: Date.now(),
                type: 'publish',
                ...activity
            };

            return [...current, newActivity];
        });
    }, []);

    // Add a function to update a pending activity with its ID
    const updatePendingActivityWithId = useCallback((metadataCID: string, stickerId: number) => {
        setPendingActivities(current =>
            current.map(activity =>
                activity.metadataCID === metadataCID
                    ? { ...activity, stickerId }
                    : activity
            )
        );
    }, []);

    // Calculate the next sticker fee based on publisher status
    useEffect(() => {
        if (goodStandingFetched && pubFeeIsFetched && repFeeIsFetched) {
            let upcomingFee = BigInt(0);
            if (goodStandingReadData && publishFeeReadData) {
                setNextStickerFee(publishFeeReadData);
                upcomingFee = publishFeeReadData;
            } else if (publishFeeReadData && repFeeReadData) {
                setNextStickerFee(publishFeeReadData + repFeeReadData);
                upcomingFee = publishFeeReadData + repFeeReadData;
            }
            setDisplayNextStickerFee(formatEther(upcomingFee));
        }
    }, [goodStandingFetched, pubFeeIsFetched, repFeeIsFetched, goodStandingReadData, publishFeeReadData, repFeeReadData]);

    const stickerValidToPublish = (
        accountIsConnected &&
        publisherDidCertify &&
        imageSize &&
        imageSize <= 800 &&
        imageContentType &&
        aspectRatio &&
        aspectRatio >= 0.5 &&
        aspectRatio <= 2 &&
        (!stickerUrl || stickerUrl.trim() === '' || stickerUrl.startsWith('https://'))
    ) ? true : false;

    // Modify the useEffect that handles transaction results
    useEffect(() => {
        if (!publishTransactionResult || !publishTransactionHashIsFetched) return;

        // Only process once per transaction
        if (processedStickerIdRef.current) return;

        console.log('Transaction result received, current state:', {
            stickerName,
            imageIpfsHash,
            publishStage,
            pendingActivities
        });

        publishTransactionResult.logs.forEach((log: any) => {
            // Check if this is a StickerDesignPublished event
            if (log.topics.length >= 4) { // StickerDesignPublished has 4 topics
                const newStickerId = log.topics[1];
                const newStickerIdNumber = newStickerId ? hexToNumber(newStickerId) : null;
                const metadataCIDBytes = log.data.slice(2); // Remove '0x' prefix
                const metadataCIDUint8Array = new Uint8Array(
                    metadataCIDBytes.match(/.{1,2}/g)?.map((byte: string) => parseInt(byte, 16)) || []
                );
                console.log('Event metadata bytes:', metadataCIDUint8Array);

                // The metadata CID is ABI-encoded with dynamic bytes
                // First 32 bytes: offset to data (should be 32)
                // Next 32 bytes: length of data
                // Then the actual data
                const dataStart = 64; // Skip offset and length
                const dataLength = metadataCIDUint8Array[63]; // Length is in the last byte of the second word
                const actualBytes = metadataCIDUint8Array.slice(dataStart, dataStart + dataLength);
                console.log('Actual metadata bytes:', actualBytes);
                const metadataCID = bs58.encode(actualBytes);
                console.log('Event metadata CID:', metadataCID);

                if (newStickerIdNumber) {
                    processedStickerIdRef.current = true;
                    console.log('Processing new sticker ID:', newStickerIdNumber);
                    console.log('Metadata CID from event:', metadataCID);
                    console.log('Pending activities:', pendingActivities);

                    // Find matching pending activity
                    const matchingActivity = pendingActivities.find(a => {
                        console.log('Comparing:', {
                            activityCID: a.metadataCID,
                            eventCID: metadataCID
                        });
                        return a.metadataCID === metadataCID;
                    });

                    if (matchingActivity) {
                        // Update the activity with its ID
                        updatePendingActivityWithId(metadataCID, newStickerIdNumber);
                        setNewStickerId(newStickerIdNumber);
                        setPublishStage('published');

                        // Notify with the matched sticker data
                        onStickerPublished({
                            stickerId: newStickerIdNumber,
                            name: matchingActivity.stickerName,
                            createdAt: new Date(matchingActivity.timestamp).toISOString(),
                            imageCID: matchingActivity.imageCID,
                            isPending: true,
                            metadataCID: matchingActivity.metadataCID
                        }, false);

                        setIsPublishing(false);
                    } else {
                        console.error('No matching pending activity found for metadata CID:', metadataCID);
                    }
                }
            }
        });
    }, [publishTransactionResult, publishTransactionHashIsFetched, pendingActivities,
        onStickerPublished, resetForm, handleClose, updatePendingActivityWithId,
        stickerName, imageIpfsHash, publishStage, setNewStickerId, setPublishStage]);

    // Reset the processed flag when the transaction hash changes
    useEffect(() => {
        processedStickerIdRef.current = false;
    }, [publishTransactionHash]);


    useEffect(() => {
        setAccountIsConnected(account.isConnected);
    }, [account.isConnected]);

    useEffect(() => {
        if (publishTransactionStatus === 'error') {
            setIsPublishing(false);
            setPublishError('failed_execution');
        }
    }, [publishTransactionStatus]);


    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files ? e.target.files[0] : null;
      if (file) {
        setFileName(file.name);
        if (file.name) {
            setStickerName(getDisplayNameFromFilename(file.name));
        }
        // Set the image size in kilobytes and round to nearest whole number
        setImageSize(Math.round(file.size / 1024));
        setImageContentType(file.type);
        setFileBlob(file);
        const reader = new FileReader();
        reader.onloadend = () => {
            const imageSrc = reader.result as string;
            setImagePreview(reader.result as string);
             // Calculate the aspect ratio
            const image = new Image();
            image.onload = () => {
                const rawAspectRatio = image.width / image.height;
                const roundedAspectRatio = Number(rawAspectRatio.toFixed(2));
                setAspectRatio(roundedAspectRatio);
            };
            image.src = imageSrc;
        };
        reader.readAsDataURL(file);
      }
    };

    const performStickerAssetUpload = async (): Promise<{metadataCID: `0x{string}`, imageCID: `0x{string}`} | null> => {
        if (!fileBlob || !stickerName) {
            console.error('Missing required data for upload');
            return null;
        }
        console.log('Starting sticker asset upload with data:', {
            name: stickerName,
            fileSize: fileBlob.size,
            fileType: fileBlob.type,
            currentImageIpfsHash: imageIpfsHash
        });

        const formData = new FormData();
        formData.append('image', fileBlob);
        formData.append('name', stickerName || '');
        formData.append('publisherDidCertify', publisherDidCertify.toString());
        formData.append('imageSize', imageSize?.toString() || '');
        formData.append('imageContentType', imageContentType || '');
        formData.append('aspectRatio', aspectRatio?.toString() || '');
        formData.append('url', stickerUrl || '');
        formData.append('description', stickerDescription || '');

        try {
            setPublishStage('uploading');
            const response = await axios.post('/stickers/createSticker', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            setPublishStage('uploaded');
            console.log('Sticker create success:', response.data);

            const stickerImageIpfsHash = response.data.imageIpfsHash;
            const stickerMetadataIpfsHash = response.data.metaIpfsHash;

            console.log('Original metadata CID from server:', stickerMetadataIpfsHash);

            // Convert the metadata CID to bytes and back to match the event format
            const metadataBytes = bs58.decode(stickerMetadataIpfsHash);
            const metadataCIDForActivity = bs58.encode(metadataBytes);

            console.log('Normalized metadata CID for activity:', metadataCIDForActivity);
            console.log('Metadata bytes:', metadataBytes);

            // Add to pending activities
            addPendingActivity({
                stickerId: 0, // Temporary ID until we get the real one
                stickerName,
                imageCID: stickerImageIpfsHash,
                metadataCID: metadataCIDForActivity
            });

            console.log('Added pending activity with metadata CID:', metadataCIDForActivity);

            setImageIpfsHash(stickerImageIpfsHash);
            setStickerMetaIpfsHash(stickerMetadataIpfsHash);
            setStickerHasBeenUploaded(true);

            return {
                metadataCID: response.data.metaIpfsHash,
                imageCID: response.data.imageIpfsHash
            }
        } catch (error) {
            setPublishStage('failed');
            setPublishError('failed_upload');
            console.error('Error uploading image:', error);
            return null;
        }
    }

    const proposeRegistrationTransactionToWallet = async (props: {metadataCID: `0x{string}`, imageCID: `0x{string}`}) => {
        const { metadataCID, imageCID } = props;
        console.log(`proposing registration transaction to wallet for metadataCID ${metadataCID} and imageCID ${imageCID}`)
        setPublishStage('awaiting_tx');
        if (StickerContractAddress == null) {
            console.error('No Sticker Contract Address');
            return;
        }
        console.log(`sticker designs contract address is ${StickerContractAddress}`)
        if (!account.address) {
            console.error('No account address');
            return;
        }
        if (!metadataCID) {
            console.error('No sticker meta IPFS hash');
            return;
        }
        const metadataBytes = bs58.decode(metadataCID)
        console.log(`metadata bytes are ${metadataBytes}`)

        // Convert the raw bytes into a format suitable for Solidity
        const bytesForContract = '0x' + Buffer.from(metadataBytes).toString('hex') as `0x${string}`;
        console.log(`hex string for contract are ${bytesForContract}`)
        console.log(`calling writeContract method of WAGMI`)
        const priceInSmallestUnit = BigInt(Math.round(premiumPrice * (10 ** paymentMethodDecimals)));
        const paymentMethodId = BigInt(paymentCoin);
        const limitToHolders = (holderAddress || zeroAddress) as `0x${string}`;
        console.log(`Price: ${priceInSmallestUnit}`);
        console.log(`Payment Method ID: ${paymentMethodId}`);
        console.log(`Limit to Holders: ${limitToHolders}`);
        console.log(`Image IPFS Hash: ${imageCID}`);
        console.log(`Next Sticker Fee: ${nextStickerFee}`);

        writeContract({
            abi,
            address: StickerContractAddress,
            functionName: 'publishStickerDesign',
            value: nextStickerFee as bigint,
            args: [{
                payoutAddress: account.address,
                price: priceInSmallestUnit,
                paymentMethodId,
                limitCount: BigInt(0),
                limitTime: BigInt(0),
                limitToHolders,
                metadataCID: bytesForContract,
                imageCID: imageCID || '',
            }],
        }, {
            onSuccess: (data, variables, context) => {
                console.log('Transaction submitted successfully:', data);
                setPublishStage('tx_sent');
                setTransactionError(null); // Clear any previous error
            },
            onError: (error, variables, context) => {
                console.error('Transaction rejected:', error);
                setPublishStage('new'); // Reset the publish stage
                setIsPublishing(false); // Allow the user to click the publish button again
                setTransactionError(error.message); // Optionally set an error message to display
            }
        });
    }

    const handleDidClickPublish = async () => {
        if (!stickerValidToPublish || isPublishing) {
            return;
        }
        setTransactionError(null); // Clear the transaction error
        setIsPublishing(true);
        console.log(`clicked publish button`);
        const uploadResult = await performStickerAssetUpload();
        if (uploadResult == null) {
            console.error('Failed to upload sticker asset');
            setIsPublishing(false);
            return;
        }
        const { metadataCID, imageCID } = uploadResult;
        if (metadataCID == null || imageCID == null) {
            console.error('Failed to upload sticker asset');
            setIsPublishing(false);
            return;
        }
        console.log(`uploaded sticker asset`);
        const requestTransactionResult = await proposeRegistrationTransactionToWallet({metadataCID, imageCID});
        console.log(`requested transaction result is ${requestTransactionResult}`);
    }

    const publishButtonClasses = stickerValidToPublish && !isPublishing
        ? 'my-2 py-4 px-16 border border-emerald-600 rounded-xl bg-emerald-600 text-white shadow-md hover:bg-emerald-500 hover:border-emerald-500 transition'
        : 'my-2 py-4 px-16 border border-slate-200 text-slate-400 rounded-xl bg-slate-100 cursor-not-allowed';

    // reset publishStage when user starts entering new data
    useEffect(() => {
        if (publishStage === 'published' && (fileBlob || stickerName || holderAddress || premiumPrice !== 0 || paymentCoin !== 0)) {
            setPublishStage('new');
        }
    }, [fileBlob, stickerName, holderAddress, premiumPrice, paymentCoin, publishStage]);

    return(
        <div className="relative flex flex-col items-center bg-gradient-to-b from-slate-50 via-white to-sky-50 text-slate-900 px-4 pb-16 min-h-screen">
            <div className={`w-full max-w-5xl mt-10 flex flex-col gap-6 px-6 md:px-8${newStickerId ? ' hidden' : ''}`}>
                <div className="flex w-full items-center justify-end">
                    <button
                        onClick={handleClose}
                        className="text-slate-700 py-2 px-3 text-xl bg-white border border-slate-200 hover:border-slate-300 hover:shadow-sm rounded-full transition"
                        aria-label="Close sticker creator"
                    >
                        ✕
                    </button>
                </div>
                <div className="space-y-6 text-center">
                    <h1 className='font-semibold text-2xl md:text-4xl tracking-tight'>Publish a Sticker</h1>
                    <p className='text-sm md:text-base leading-relaxed text-slate-700 max-w-3xl mx-auto'>
                        Publish a new sticker to StickerChain and earn onchain every time it is slapped.
                    </p>
                </div>
            </div>

            <div className={`w-full max-w-5xl mt-10 space-y-10${newStickerId ? ' hidden' : ''}`}>
                {!newStickerId && (
                    <>
                        <StickerContentUploader
                            handleImageChange={handleImageChange}
                            imagePreview={imagePreview}
                            imageSize={imageSize}
                            imageContentType={imageContentType}
                            aspectRatio={aspectRatio}
                            stickerName={stickerName}
                            setStickerName={setStickerName}
                            stickerUrl={stickerUrl}
                            setStickerUrl={setStickerUrl}
                            stickerDescription={stickerDescription}
                            setStickerDescription={setStickerDescription}
                            boardRef={boardRef}
                            premiumPrice={premiumPrice}
                            setPremiumPrice={setPremiumPrice}
                            paymentCoin={paymentCoin}
                            setPaymentCoin={setPaymentCoin}
                            startAddingToken={handleStartAddingToken}
                            forceStackedHeader={isMiniApp}
                        />

                        <StickerPublisherCertifications setPublisherDidCertify={setPublisherDidCertify} publisherDidCertify={publisherDidCertify} />

                        <div className='p-6 md:p-8 w-full bg-white border border-slate-200 rounded-2xl shadow-sm'>
                            <h2 className='text-xl font-semibold text-slate-900 text-center'>{accountIsConnected ? 'Publisher Account' : 'Connect Publisher Account'}</h2>
                            <div className="mt-4 flex justify-center">
                                <ConnectAccount />
                            </div>
                        </div>

                        <div className='mt-2 text-center bg-white border border-slate-200 rounded-2xl shadow-sm p-8'>
                            <button
                                disabled={!stickerValidToPublish || isPublishing}
                                onClick={handleDidClickPublish}
                                className={publishButtonClasses}
                                aria-label="Publish Sticker"
                            >
                                {isPublishing ? 'Publishing...' : 'Publish Sticker'}
                            </button>

                            <h2 className='mt-6 text-lg font-medium text-slate-700'>
                                {displayStage(publishStage)}
                            </h2>
                        </div>
                    </>
                )}

                {publishedStickers.length > 0 && (
                    <div className="w-full bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
                        <h2 className="text-2xl mb-4 text-center font-semibold">Your Latest Stickers</h2>
                        <PublishedStickerList stickers={publishedStickers} />
                    </div>
                )}

                {transactionError && (
                    <div className='text-red-600 text-center mt-4 md:mx-12'>
                        Transaction Error: {transactionError.split('\n')[0]}
                    </div>
                )}
            </div>

            {newStickerId && (
                <div className="w-full max-w-3xl mt-16 flex flex-col items-center">
                    <div className="w-full rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
                        <div className="flex w-full items-center justify-between">
                            <h2 className="text-xl font-semibold text-slate-800">Share your sticker</h2>
                            <button
                                onClick={handleClose}
                                className="text-slate-600 hover:text-slate-900 transition"
                                aria-label="Close share panel"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="mt-4">
                            <StickerSharePanel
                                stickerId={newStickerId}
                                stickerName={stickerName}
                                isMiniApp={isMiniApp}
                                onClose={handleClose}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
